import { alpha, createTheme } from '@mui/material/styles';

/**
 * RunnrZ Admin — design tokens.
 * Fond clair et neutre, un seul accent (orange Runnrz), tableaux denses.
 * Les couleurs sont choisies pour un contraste ≥ 4.5:1 sur blanc.
 */
export const tokens = {
  bg: '#F6F6F3',
  surface: '#FFFFFF',
  surfaceMuted: '#FAFAF8',
  line: '#E7E5E0',
  lineSoft: '#F0EFEB',
  lineStrong: '#DAD8D2',
  ink: '#1A1A19',
  ink2: '#45443F',
  muted: '#6B6A64',
  accent: '#E0592F',
  accentStrong: '#B2441F',
  accentText: '#A33E1C',
  accentSoft: '#FCEDE6',
  blue: '#2C64C8',
  blueSoft: '#E9F0FC',
  green: '#1C7445',
  greenSoft: '#E3F2E9',
  amber: '#8A5300',
  amberSoft: '#FBF1DE',
  red: '#B3302A',
  redSoft: '#FBEAE8',
  segmented: '#EDECE8',
} as const;

export const fontSans = ['Geist', 'ui-sans-serif', 'system-ui', '-apple-system', '"Segoe UI"', 'sans-serif'].join(',');
export const fontMono = ['"Geist Mono"', 'ui-monospace', 'SFMono-Regular', 'Menlo', 'monospace'].join(',');

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.accentStrong,
      light: tokens.accent,
      dark: '#93371A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: tokens.ink,
      light: tokens.ink2,
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    info: { main: tokens.blue, light: tokens.blueSoft, contrastText: '#FFFFFF' },
    success: { main: tokens.green, light: tokens.greenSoft, contrastText: '#FFFFFF' },
    warning: { main: tokens.amber, light: tokens.amberSoft, contrastText: '#FFFFFF' },
    error: { main: tokens.red, light: tokens.redSoft, contrastText: '#FFFFFF' },
    background: {
      default: tokens.bg,
      paper: tokens.surface,
    },
    text: {
      primary: tokens.ink,
      secondary: tokens.muted,
      disabled: '#A8A6A0',
    },
    divider: tokens.line,
    action: {
      hover: alpha(tokens.ink, 0.04),
      selected: tokens.accentSoft,
      focus: alpha(tokens.blue, 0.12),
    },
  },
  typography: {
    fontFamily: fontSans,
    fontSize: 14,
    h1: { fontSize: '2rem', fontWeight: 600, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.02em' },
    h3: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em' },
    h4: { fontSize: '1.625rem', fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontSize: '0.9375rem', fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500, fontSize: '0.8125rem' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.84375rem' },
    caption: { fontSize: '0.75rem', color: tokens.muted },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em' },
    button: { textTransform: 'none', fontWeight: 500, fontSize: '0.84375rem' },
  },
  shape: {
    borderRadius: 9,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(20,20,19,.06)',
    '0 1px 3px rgba(20,20,19,.08)',
    '0 2px 6px rgba(20,20,19,.08)',
    '0 4px 12px rgba(20,20,19,.08)',
    '0 6px 16px rgba(20,20,19,.10)',
    '0 8px 24px rgba(20,20,19,.12)',
    ...Array(18).fill('0 12px 32px rgba(20,20,19,.14)'),
  ] as unknown as ReturnType<typeof createTheme>['shadows'],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg,
          fontFamily: fontSans,
          fontFeatureSettings: '"ss01"',
        },
        'code, kbd': { fontFamily: fontMono },
        '.num': { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' },
        '*:focus-visible': {
          outline: `2px solid ${tokens.blue}`,
          outlineOffset: 2,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 9, minHeight: 36, paddingInline: 14 },
        sizeSmall: { minHeight: 30, paddingInline: 10, fontSize: '0.78125rem' },
        sizeLarge: { minHeight: 44, fontSize: '0.9375rem' },
        outlined: {
          borderColor: tokens.line,
          color: tokens.ink,
          backgroundColor: tokens.surface,
          '&:hover': { borderColor: tokens.lineStrong, backgroundColor: tokens.bg },
        },
        outlinedError: { color: tokens.red, '&:hover': { borderColor: alpha(tokens.red, 0.4), backgroundColor: tokens.redSoft } },
        text: { '&:hover': { backgroundColor: alpha(tokens.ink, 0.04) } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8, color: tokens.ink2 },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0, variant: 'outlined' },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: tokens.line },
        rounded: { borderRadius: 12 },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: { borderRadius: 12, borderColor: tokens.line, boxShadow: 'none' },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '16px 20px 8px' },
        title: { fontSize: '0.9375rem', fontWeight: 600 },
        subheader: { fontSize: '0.78125rem' },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: '16px 20px', '&:last-child': { paddingBottom: 18 } } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          backgroundColor: tokens.surface,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.lineStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B9B7B0' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: tokens.accentStrong, borderWidth: 1.5 },
        },
        input: { fontSize: '0.84375rem' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '0.84375rem' } },
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { border: `1px solid ${tokens.line}`, boxShadow: '0 8px 24px rgba(20,20,19,.12)' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: 999,
          fontWeight: 500,
          fontSize: '0.71875rem',
          height: 24,
          ...(ownerState.variant === 'filled' && (!ownerState.color || ownerState.color === 'default')
            ? { backgroundColor: tokens.lineSoft, color: tokens.ink2 }
            : {}),
        }),
        sizeSmall: { height: 22 },
        outlined: { borderColor: tokens.line },
        colorSuccess: { backgroundColor: tokens.greenSoft, color: tokens.green },
        colorWarning: { backgroundColor: tokens.amberSoft, color: tokens.amber },
        colorError: { backgroundColor: tokens.redSoft, color: tokens.red },
        colorInfo: { backgroundColor: tokens.blueSoft, color: tokens.blue },
        colorPrimary: { backgroundColor: tokens.accentSoft, color: tokens.accentText },
      },
    },
    MuiTableContainer: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: tokens.lineSoft, fontSize: '0.84375rem', paddingTop: 10, paddingBottom: 10 },
        head: {
          fontSize: '0.75rem',
          fontWeight: 500,
          color: tokens.muted,
          backgroundColor: tokens.surfaceMuted,
          borderBottomColor: tokens.line,
          whiteSpace: 'nowrap',
        },
        sizeSmall: { paddingTop: 8, paddingBottom: 8 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&.MuiTableRow-hover:hover': { backgroundColor: tokens.surfaceMuted } },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { borderTop: `1px solid ${tokens.line}` },
        toolbar: { minHeight: 48 },
        selectLabel: { fontSize: '0.8125rem', color: tokens.muted },
        displayedRows: { fontSize: '0.8125rem', color: tokens.muted },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42, borderBottom: `1px solid ${tokens.line}` },
        indicator: { backgroundColor: tokens.accentStrong, height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 42,
          minWidth: 0,
          paddingInline: 2,
          marginRight: 22,
          textTransform: 'none',
          fontSize: '0.84375rem',
          fontWeight: 400,
          color: tokens.muted,
          '&.Mui-selected': { color: tokens.ink, fontWeight: 500 },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { backgroundColor: tokens.segmented, padding: 3, borderRadius: 10, gap: 2 },
        grouped: { border: 0, borderRadius: '8px !important', margin: 0 },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          border: 0,
          color: '#55544E',
          fontSize: '0.8125rem',
          paddingBlock: 5,
          paddingInline: 12,
          '&.Mui-selected': {
            backgroundColor: tokens.surface,
            color: tokens.ink,
            fontWeight: 500,
            boxShadow: '0 1px 2px rgba(20,20,19,.08)',
            '&:hover': { backgroundColor: tokens.surface },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: { '&.Mui-checked': { color: '#FFFFFF' }, '&.Mui-checked + .MuiSwitch-track': { backgroundColor: tokens.green, opacity: 1 } },
        track: { backgroundColor: '#D9D7D0', opacity: 1 },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#B9B7B0', '&.Mui-checked': { color: tokens.accentStrong } } },
    },
    MuiFab: {
      styleOverrides: {
        root: { boxShadow: '0 6px 18px rgba(178,68,31,.28)', '&:hover': { boxShadow: '0 8px 22px rgba(178,68,31,.34)' } },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: tokens.lineStrong,
          '&.Mui-active': { color: tokens.accentStrong },
          '&.Mui-completed': { color: tokens.green },
        },
        text: { fontFamily: fontMono, fontSize: '0.75rem', fontWeight: 500 },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.9375rem',
          fontWeight: 500,
          color: tokens.muted,
          '&.Mui-active': { color: tokens.ink, fontWeight: 600 },
          '&.Mui-completed': { color: tokens.ink2, fontWeight: 500 },
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: { line: { borderColor: tokens.line } },
    },
    MuiStepContent: {
      styleOverrides: { root: { borderLeftColor: tokens.line } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 14, boxShadow: '0 20px 50px rgba(20,20,19,.25)' } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '1.0625rem', fontWeight: 600, padding: '20px 24px 8px' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 24px 20px', gap: 8 } },
    },
    MuiPopover: {
      styleOverrides: { paper: { boxShadow: '0 8px 24px rgba(20,20,19,.12)' } },
    },
    MuiPopper: {
      styleOverrides: { root: { '& .MuiPaper-root': { boxShadow: '0 8px 24px rgba(20,20,19,.12)' } } },
    },
    MuiMenu: {
      styleOverrides: { paper: { border: `1px solid ${tokens.line}`, boxShadow: '0 8px 24px rgba(20,20,19,.12)', borderRadius: 10 } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: '0.84375rem', borderRadius: 6, marginInline: 6, minHeight: 36 } },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: tokens.ink, fontSize: '0.75rem', borderRadius: 6 } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontSize: '0.8125rem' },
        standardSuccess: { backgroundColor: tokens.greenSoft, color: '#145534' },
        standardWarning: { backgroundColor: tokens.amberSoft, color: '#6E4300' },
        standardError: { backgroundColor: tokens.redSoft, color: '#8F2620' },
        standardInfo: { backgroundColor: tokens.blueSoft, color: '#1F4E9E' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 999, backgroundColor: tokens.lineSoft, height: 6 }, bar: { borderRadius: 999 } },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: tokens.line } },
    },
    MuiAvatar: {
      styleOverrides: { root: { fontWeight: 600, fontSize: '0.8125rem' } },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { borderRightColor: tokens.line } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: { root: { backgroundColor: tokens.surface, borderBottom: `1px solid ${tokens.line}` } },
    },
  },
});
